import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomButton from "../../../../components/CustomButton/Button";

export interface LegState {
  sending: boolean;
  verifying: boolean;
  otp: string;
  /** epoch ms of the last successful send — drives the "expires in" hint */
  sentAt: number | null;
  /** set after OTP_EXPIRED / OTP_MAX_ATTEMPTS: retyping cannot succeed */
  mustResend: boolean;
  /** epoch ms until which a 429 forbids another send */
  lockedUntil: number | null;
  error: string | null;
  hint: string | null;
}

/**
 * CustomButton is a styled-components Button carrying `margin-top: 11px`.
 * MUI's `sx` is emotion, so a plain `mt: 0` has equal specificity in a
 * different stylesheet and loses the cascade — the button then hangs ~11px
 * below whatever input it sits beside. Force it off.
 */
const ROW_BUTTON = { marginTop: "0 !important" };

export const initialLegState: LegState = {
  sending: false,
  verifying: false,
  otp: "",
  sentAt: null,
  mustResend: false,
  lockedUntil: null,
  error: null,
  hint: null
};

interface OtpProofCardProps {
  step: number;
  title: string;
  /** what the agent should say out loud, e.g. the masked mobile */
  target: string;
  instruction: string;
  verified: boolean;
  state: LegState;
  /** approximate code lifetime in seconds — backend value is env-configurable */
  lifetimeSeconds: number;
  /** ticking clock, only supplied while a countdown is on screen */
  now: number;
  disabled: boolean;
  onOtpChange: (otp: string) => void;
  onSend: () => void;
  onVerify: () => void;
}

const formatClock = (totalSeconds: number) => {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const OtpProofCard: React.FC<OtpProofCardProps> = ({
  step,
  title,
  target,
  instruction,
  verified,
  state,
  lifetimeSeconds,
  now,
  disabled,
  onOtpChange,
  onSend,
  onVerify
}) => {
  const rateLimitedFor = state.lockedUntil
    ? Math.ceil((state.lockedUntil - now) / 1000)
    : 0;
  const isRateLimited = rateLimitedFor > 0;

  const expiresIn = state.sentAt
    ? Math.ceil((state.sentAt + lifetimeSeconds * 1000 - now) / 1000)
    : 0;
  const codeOutstanding = expiresIn > 0 && !state.mustResend;

  const busy = state.sending || state.verifying;
  const otpComplete = /^\d{6}$/.test(state.otp);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: "8px",
        borderColor: verified ? "success.light" : "divider",
        backgroundColor: verified ? "rgba(46, 125, 50, 0.04)" : "inherit",
        opacity: disabled && !verified ? 0.6 : 1,
        height: "100%"
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={0.5}
      >
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontWeight: 600,
            fontSize: "14px",
            color: "#192F67"
          }}
        >
          {step}. {title}
        </Typography>
        {verified ? (
          <Chip
            size="small"
            color="success"
            icon={<CheckCircleIcon />}
            label="Verified"
          />
        ) : (
          <Chip size="small" variant="outlined" label="Pending" />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {instruction}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Montserrat",
          fontWeight: 600,
          fontSize: "15px",
          mb: 2,
          wordBreak: "break-all"
        }}
      >
        {target}
      </Typography>

      {verified ? (
        <Typography variant="body2" color="success.main">
          Proof recorded. Nothing further needed on this leg.
        </Typography>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 1, mb: 1.5 }}
          >
            <CustomButton
              callBack={onSend}
              disabled={disabled || busy || isRateLimited}
              styles={{
                ...ROW_BUTTON,
                minHeight: "36px",
                minWidth: "130px",
                flexShrink: 0
              }}
            >
              {state.sending ? (
                <CircularProgress size={18} color="inherit" />
              ) : state.sentAt ? (
                "Resend code"
              ) : (
                "Send code"
              )}
            </CustomButton>
            {isRateLimited ? (
              <Typography variant="caption" color="error">
                Rate limited — try again in {formatClock(rateLimitedFor)}
              </Typography>
            ) : codeOutstanding ? (
              <Typography variant="caption" color="text.secondary">
                Code sent · expires in ~{formatClock(expiresIn)}
              </Typography>
            ) : state.sentAt ? (
              <Typography variant="caption" color="warning.main">
                No code outstanding — send a new one
              </Typography>
            ) : null}
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 1.5 }}
          >
            <TextField
              size="small"
              label="6-digit code"
              value={state.otp}
              onChange={(e) =>
                onOtpChange(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && otpComplete && !state.mustResend) {
                  onVerify();
                }
              }}
              disabled={disabled || busy || state.mustResend}
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
              sx={{ flex: "1 1 120px", maxWidth: "170px" }}
            />
            <CustomButton
              callBack={onVerify}
              disabled={disabled || busy || state.mustResend || !otpComplete}
              styles={{
                ...ROW_BUTTON,
                minHeight: "40px",
                minWidth: "100px",
                flexShrink: 0
              }}
            >
              {state.verifying ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Verify"
              )}
            </CustomButton>
          </Stack>

          {state.error && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {state.error}
              {state.hint && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {state.hint}
                </Typography>
              )}
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
};

export default OtpProofCard;
